import React, { useEffect, useRef, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkle, Star, Warning, Lightbulb, ArrowRight, Key, CheckCircle, ShareNetwork, GitDiff, TrendUp, TrendDown } from "@phosphor-icons/react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Disclaimer } from "@/components/Disclaimer";
import { ConsultBlock } from "@/components/ConsultCTA";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export default function Result() {
  const { resultId } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const initial = location.state || {};
  const [doc, setDoc] = useState(initial.result ? { result: initial.result, narrative: null, narrative_status: "pending" } : null);
  const [loading, setLoading] = useState(!initial.result);
  const [delta, setDelta] = useState(null);
  const [copied, setCopied] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchOnce = async () => {
      try {
        const { data } = await api.get(`/results/${resultId}`);
        setDoc(data);
        if (data.narrative_status === "ready" || data.narrative_status === "failed") {
          clearInterval(pollRef.current);
        }
      } catch {
        clearInterval(pollRef.current);
      } finally {
        setLoading(false);
      }
    };
    fetchOnce();
    pollRef.current = setInterval(fetchOnce, 3500);
    return () => clearInterval(pollRef.current);
  }, [resultId]);

  useEffect(() => {
    const slug = doc?.assessment_slug || doc?.result?.assessment_slug;
    if (!slug || !user) return;
    api.get(`/assessments/${slug}/delta`).then(({ data }) => { if (data.has_delta) setDelta(data); }).catch(() => {});
  }, [doc?.assessment_slug, doc?.result?.assessment_slug, user]);

  const shareUrl = `${window.location.origin}/s/${resultId}`;
  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: "My pattern on TheInner.app", url: shareUrl });
        return;
      }
    } catch { /* fall through to copy */ }
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  if (loading && !doc) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-950"><div className="h-10 w-10 animate-spin rounded-full border-2 border-slate-800 border-t-teal-400" /></div>;
  }
  if (!doc) return null;

  const r = doc.result;
  const primary = r.primary;
  const color = primary.color;
  const narrativeReady = doc.narrative_status === "ready" && doc.narrative?.length;
  const narrativeFailed = doc.narrative_status === "failed";

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />
      <div className="mx-auto max-w-4xl px-5 pb-24 pt-32 sm:px-8">
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="text-xs font-bold uppercase tracking-[0.2em] text-teal-500">
          Your {r.assessment_name} Result
        </motion.p>

        {/* Account banner */}
        {initial.new_account && initial.temp_password && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-5 flex items-start gap-3 rounded-xl border border-teal-500/30 bg-teal-500/5 p-4" data-testid="account-banner">
            <Key size={18} weight="duotone" className="mt-0.5 shrink-0 text-teal-400" />
            <div className="text-sm text-slate-300">
              <p className="font-medium text-teal-300">We created your free account.</p>
              <p className="mt-1 text-slate-400">Your temporary password is <span className="font-mono text-teal-300">{initial.temp_password}</span>. We also emailed it to you. You can change it anytime in <Link to="/dashboard" className="underline">Settings</Link>.</p>
            </div>
          </motion.div>
        )}

        {/* Primary card */}
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="relative mt-5 overflow-hidden rounded-3xl border border-slate-800 bg-slate-900 p-8 sm:p-12" data-testid="result-primary-card">
          <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full opacity-25 blur-3xl" style={{ background: color }} />
          <div className="relative">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500">You are</span>
            <h1 className="mt-3 font-heading text-4xl font-light tracking-tight sm:text-6xl" style={{ color }} data-testid="result-primary-name">{primary.name}</h1>
            <p className="mt-3 text-lg italic text-slate-400">&quot;{primary.tagline}&quot;</p>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300">{primary.summary}</p>
            <div className="mt-6 flex flex-wrap items-center gap-3 text-sm">
              {r.secondary && <span className="rounded-full border border-slate-700 bg-slate-950 px-3 py-1 text-slate-400">Secondary: <span style={{ color: r.secondary.color }}>{r.secondary.name}</span></span>}
              <span className="rounded-full border border-slate-700 bg-slate-950 px-3 py-1 text-slate-400">Confidence: <span className="text-teal-300">{r.confidence}%</span></span>
            </div>
          </div>
        </motion.div>

        {/* Share + reassessment delta */}
        <div className="mt-5 flex flex-wrap items-center gap-3" data-testid="result-share-row">
          <button onClick={handleShare} className="inline-flex items-center gap-2 rounded-full border border-teal-500/40 bg-teal-500/5 px-5 py-2.5 text-sm font-medium text-teal-300 transition-all hover:bg-teal-500/10" data-testid="result-share-btn">
            {copied ? <><CheckCircle size={15} weight="fill" /> Link copied</> : <><ShareNetwork size={15} weight="duotone" /> Share my result</>}
          </button>
          <a href={`/s/${resultId}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-medium text-slate-400 hover:text-teal-300" data-testid="result-share-preview">See shareable card <ArrowRight size={13} weight="bold" /></a>
        </div>

        {delta && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-5 rounded-2xl border border-sky-500/30 bg-sky-500/5 p-6" data-testid="result-delta">
            <div className="flex items-center gap-2"><GitDiff size={16} weight="duotone" className="text-sky-400" /><p className="text-xs font-bold uppercase tracking-[0.2em] text-sky-400">You've changed since {delta.previous.date}</p></div>
            {delta.primary_changed ? (
              <p className="mt-2 text-sm text-slate-200">Your dominant pattern shifted from <span className="text-slate-400">{delta.previous.primary}</span> to <span className="text-sky-300">{delta.latest.primary}</span>.</p>
            ) : (
              <p className="mt-2 text-sm text-slate-200">Your dominant pattern is still <span className="text-sky-300">{delta.latest.primary}</span> — but the balance underneath has shifted.</p>
            )}
            {delta.changes.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {delta.changes.map((c) => (
                  <span key={c.name} className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs ${c.diff > 0 ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-300" : "border-rose-500/30 bg-rose-500/5 text-rose-300"}`} data-testid={`delta-${c.name}`}>
                    {c.diff > 0 ? <TrendUp size={12} weight="bold" /> : <TrendDown size={12} weight="bold" />} {c.name} {c.diff > 0 ? "+" : ""}{c.diff}
                  </span>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Breakdown */}
        <div className="mt-6 rounded-2xl border border-slate-800 bg-slate-900 p-6" data-testid="result-breakdown">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500">Your pattern breakdown</p>
          <div className="mt-4 space-y-3">
            {r.breakdown.map((b) => (
              <div key={b.key}>
                <div className="flex justify-between text-xs"><span className="text-slate-300">{b.name}</span><span className="font-mono text-slate-500">{b.score}</span></div>
                <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-slate-800"><div className="h-full rounded-full" style={{ width: `${b.score}%`, background: b.color }} /></div>
              </div>
            ))}
          </div>
        </div>

        {/* Strengths + growth */}
        <div className="mt-6 grid gap-6 md:grid-cols-2">
          <Panel icon={Star} title="Your strengths" tone="teal" testid="result-strengths">
            <ul className="space-y-2.5">{primary.strengths.map((s, i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-300"><Sparkle size={15} weight="fill" className="mt-0.5 shrink-0 text-teal-400" /> {s}</li>)}</ul>
          </Panel>
          <Panel icon={Warning} title="Your growth edges" tone="amber" testid="result-growth">
            <ul className="space-y-2.5">{primary.growth.map((s, i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-300"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" /> {s}</li>)}</ul>
          </Panel>
        </div>

        {/* Key insight */}
        <div className="mt-6 rounded-2xl border p-6" style={{ borderColor: `${color}40`, background: `${color}0d` }} data-testid="result-insight">
          <div className="flex items-center gap-3"><Lightbulb size={20} weight="duotone" style={{ color }} /><h3 className="font-heading text-base font-medium text-slate-100">The key insight</h3></div>
          <p className="mt-3 text-base leading-relaxed text-slate-200">{primary.insight}</p>
        </div>

        {/* AI narrative */}
        <div className="mt-10" data-testid="result-narrative">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-500">Your personalized analysis</p>
          {!narrativeReady && !narrativeFailed && (
            <div className="mt-4 flex items-center gap-3 rounded-2xl border border-slate-800 bg-slate-900 p-6" data-testid="narrative-loading">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-slate-700 border-t-teal-400" />
              <p className="text-sm text-slate-400">Writing your deeper, personalized analysis… this takes a few seconds.</p>
            </div>
          )}
          {narrativeFailed && (
            <p className="mt-4 text-sm text-slate-500">Your personalized analysis couldn't be generated right now — your full result above is complete. Try refreshing in a moment.</p>
          )}
          {narrativeReady && (
            <div className="mt-4 space-y-5">
              {doc.narrative.map((sec, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }} className="rounded-2xl border border-slate-800 bg-slate-900 p-6 sm:p-8" data-testid={`narrative-section-${i}`}>
                  <div className="flex items-center gap-3"><span className="font-mono text-sm text-teal-400">{String(i + 1).padStart(2, "0")}</span><h3 className="font-heading text-xl font-medium text-slate-100">{sec.title}</h3></div>
                  <div className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-slate-300">{sec.body}</div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Cross-pattern connection (v2) */}
        {doc.patterns?.length > 0 && (
          <div className="mt-6 rounded-2xl border border-teal-500/20 bg-teal-500/5 p-6" data-testid="result-patterns">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-500">How this connects to your bigger picture</p>
            <p className="mt-2 text-sm text-slate-400">This result maps to these recurring inner patterns. The more assessments you take, the more the platform shows you where they overlap.</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {doc.patterns.map((p) => (
                <span key={p.id} className="rounded-full border border-teal-500/30 bg-slate-950 px-3 py-1.5 text-sm text-teal-200" title={p.blurb} data-testid={`result-pattern-${p.id}`}>{p.name}</span>
              ))}
            </div>
            {user && <Link to="/dashboard" className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-teal-400 hover:text-teal-300">See your living profile <ArrowRight size={14} weight="bold" /></Link>}
          </div>
        )}

        {/* Consult */}
        <div className="mt-10"><ConsultBlock source={`result-${r.assessment_slug}`} /></div>

        {/* Save / dashboard */}
        <div className="mt-8 rounded-2xl border border-slate-800 bg-slate-900/50 p-6 text-center" data-testid="result-dashboard-cta">
          <CheckCircle size={28} weight="duotone" className="mx-auto text-teal-400" />
          <p className="mt-3 text-sm text-slate-400">This result is saved to your living profile. Take more assessments and talk to your AI coach inside your dashboard.</p>
          <div className="mt-4 flex flex-wrap justify-center gap-3">
            <Link to="/dashboard" className="inline-flex items-center gap-2 rounded-full bg-teal-500 px-6 py-2.5 text-sm font-semibold text-slate-950 hover:bg-teal-400" data-testid="result-to-dashboard">Open Dashboard <ArrowRight size={15} weight="bold" /></Link>
            <Link to="/#all" className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-6 py-2.5 text-sm font-medium text-slate-200 hover:border-slate-500">Take Another Assessment</Link>
          </div>
        </div>

        <div className="mt-10"><Disclaimer /></div>
      </div>
      <Footer />
    </div>
  );
}

function Panel({ icon: Icon, title, children, tone, testid }) {
  const toneClass = tone === "amber" ? "text-amber-400 bg-amber-500/10" : "text-teal-400 bg-teal-500/10";
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900 p-6" data-testid={testid}>
      <div className="flex items-center gap-3"><span className={`flex h-9 w-9 items-center justify-center rounded-lg ${toneClass}`}><Icon size={18} weight="duotone" /></span><h3 className="font-heading text-base font-medium text-slate-100">{title}</h3></div>
      <div className="mt-4">{children}</div>
    </div>
  );
}
